import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { registryAPI } from '../services/api';
import RegistryItemCard from './RegistryItemCard';
import RegistryItemRow from './RegistryItemRow';
import DetailModal from './DetailModal';
import EmptyState from './EmptyState';
import { IconExclamationCircle } from './Icons';

const DEFAULT_PER_PAGE = 6;

function PaginationBar({ currentPage, totalPages, total, startItem, endItem, searchParams, perPage }) {
  const [, setSearchParams] = useSearchParams();
  const goToPage = (targetPage) => {
    const page = Math.max(1, Math.min(targetPage, totalPages));
    const next = new URLSearchParams(searchParams);
    next.set('page', String(page));
    next.set('per_page', String(perPage));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
  return (
    <div className="ds-pagination-bar">
      <p className="ds-pagination-bar__info">
        Showing <strong>{startItem}–{endItem}</strong> of <strong>{total}</strong> companies
      </p>
      <div className="ds-pagination-bar__pages">
        {pages.map((p) => (
          <button
            key={p}
            type="button"
            className={`ds-pagination-bar__page-btn ${p === currentPage ? 'ds-pagination-bar__page-btn--active' : ''}`}
            onClick={() => goToPage(p)}
            aria-label={`Page ${p}`}
            aria-current={p === currentPage ? 'page' : undefined}
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}

const RegistryList = ({ viewMode = 'grid' }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [descriptors, setDescriptors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDescriptor, setSelectedDescriptor] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: DEFAULT_PER_PAGE,
    total: 0,
    total_pages: 0,
    has_prev: false,
    has_next: false
  });

  const fetchDescriptors = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const searchQuery = searchParams.get('search') || '';
      const page = parseInt(searchParams.get('page'), 10) || 1;
      const perPage = parseInt(searchParams.get('per_page'), 10) || DEFAULT_PER_PAGE;
      const response = await registryAPI.getDescriptors({
        page,
        per_page: perPage,
        search: searchQuery
      });
      const data = response.data;
      if (data && data.success !== false) {
        const list = data.data || [];
        const pag = data.pagination || {};
        const total = pag.total || list.length;
        const totalPages = pag.total_pages || Math.ceil(total / (pag.per_page || perPage));
        setDescriptors(list);
        setPagination({
          page: pag.page || page,
          per_page: pag.per_page || perPage,
          total,
          total_pages: totalPages,
          has_prev: (pag.page || page) > 1,
          has_next: (pag.page || page) < totalPages
        });
      } else {
        throw new Error(data?.error || 'Failed to load data providers');
      }
    } catch (err) {
      setError('Failed to load data providers');
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchDescriptors();
  }, [searchParams, fetchDescriptors]);

  if (loading) {
    return (
      <div className="ds-state ds-state--loading">
        <div className="ds-state__spinner" role="status" aria-hidden="false">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="ds-state__text">Loading data providers</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ds-state ds-state--error">
        <span className="ds-state__icon-wrap">
          <IconExclamationCircle className="ds-state__icon" aria-hidden />
        </span>
        <h3 className="ds-state__title">Could not load data providers</h3>
        <p className="ds-state__text">{error}</p>
      </div>
    );
  }

  if (descriptors.length === 0) {
    const activeQuery = searchParams.get('search') || '';
    return (
      <EmptyState
        query={activeQuery}
        onClearSearch={activeQuery ? () => setSearchParams(new URLSearchParams()) : undefined}
      />
    );
  }

  const { page, per_page, total, total_pages } = pagination;
  const startItem = total === 0 ? 0 : (page - 1) * per_page + 1;
  const endItem = total === 0 ? 0 : Math.min(page * per_page, total);

  return (
    <>
      <div className="ds-content-body">
        <div className="ds-stats">
          <div className="ds-stat-card">
            <p className="ds-stat-card__label">Total Companies</p>
            <p className="ds-stat-card__value">{total}</p>
          </div>
          <div className="ds-stat-card">
            <p className="ds-stat-card__label">Current Page</p>
            <p className="ds-stat-card__value">
              {page}
              <span className="ds-stat-card__suffix"> / {total_pages}</span>
            </p>
          </div>
          <div className="ds-stat-card">
            <p className="ds-stat-card__label">View Mode</p>
            <p className="ds-stat-card__value" style={{ textTransform: 'capitalize' }}>{viewMode}</p>
          </div>
        </div>

        {viewMode === 'grid' ? (
          <div className="ds-grid-modern">
            {descriptors.map((descriptor, index) => (
              <RegistryItemCard
                key={descriptor.id || index}
                descriptor={descriptor}
                onSelect={setSelectedDescriptor}
              />
            ))}
          </div>
        ) : (
          <div className="ds-list-table-wrap">
            <table className="ds-list-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Industry</th>
                  <th>Domain</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {descriptors.map((descriptor, index) => (
                  <RegistryItemRow
                    key={descriptor.id || index}
                    descriptor={descriptor}
                    onSelect={setSelectedDescriptor}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total_pages > 1 && (
          <PaginationBar
            currentPage={page}
            totalPages={total_pages}
            total={total}
            startItem={startItem}
            endItem={endItem}
            searchParams={searchParams}
            perPage={per_page}
          />
        )}
      </div>

      {selectedDescriptor && (
        <DetailModal
          descriptor={selectedDescriptor}
          onClose={() => setSelectedDescriptor(null)}
        />
      )}
    </>
  );
};

export default RegistryList;
